import {
	type BillSnapshot,
	COMPOSITION_DECLARATION,
	upiLink,
	upiReference,
} from "@luckymenu/billing-core";
import type { PaymentMethod } from "@luckymenu/core";
import type { Column } from "../doc";
import type { PrintDocument, StoredBlock } from "../document";
import {
	centredLines,
	copyLabelBlock,
	dateAndTime,
	money,
	PAYMENT_LABELS,
	pair,
	percent,
	signedMoney,
	sumLine,
} from "./format";

/**
 * The bill (ux-design.md, Printed output; ordering-billing-gst.md, GST):
 * logo or name, address, phone, GSTIN, FSSAI; bill number, date, table,
 * cashier; items with quantity, rate and amount; subtotal, discount, taxes
 * by rate, round-off; the total in double height; payments; the UPI QR for
 * what is left to pay; the footer.
 *
 * | GST mode    | Title          | Prints                                       |
 * | none        | BILL           | no tax lines                                 |
 * | composition | BILL OF SUPPLY | the composition declaration, GSTIN, no tax   |
 * | regular     | TAX INVOICE    | GSTIN, SAC, taxable value, CGST and SGST by rate |
 *
 * Everything comes from the bill snapshot, so a reprint matches the original
 * whatever changed since.
 */

export type BillPrintOptions = {
	/** "Duplicate" on a reprint (ordering-billing-gst.md, Bill immutability). */
	copyLabel?: string | null;
	/** The bill's ID, for the UPI reference (`tr`). */
	billId?: string | null;
	/** The person who finalised it. */
	cashierName?: string | null;
	/** Confirmed payments so far, oldest first. */
	payments?: { method: PaymentMethod; amountPaise: number }[];
	/** The restaurant's logo file, printed above its name (PR-09, P5-T27). */
	logoFileId?: string | null;
};

const TITLES: Record<BillSnapshot["kind"], string> = {
	tax_invoice: "TAX INVOICE",
	bill_of_supply: "BILL OF SUPPLY",
	plain: "BILL",
};

function sellerBlocks(s: BillSnapshot["seller"]): StoredBlock[] {
	const b: StoredBlock[] = [
		{ t: "text", text: s.name, style: { align: "center", bold: true, w: 2, h: 2 } },
	];
	b.push(...centredLines(s.header));
	if (s.legalName && s.legalName !== s.name) {
		b.push({ t: "text", text: s.legalName, style: { align: "center" } });
	}
	const cityLine = [s.city, s.pincode].filter(Boolean).join(" ");
	for (const line of [s.addressLine1, s.addressLine2, cityLine]) {
		if (line) b.push({ t: "text", text: line, style: { align: "center" } });
	}
	if (s.phone) b.push({ t: "text", text: `Ph: ${s.phone}`, style: { align: "center" } });
	if (s.gstin) b.push({ t: "text", text: `GSTIN: ${s.gstin}`, style: { align: "center" } });
	if (s.fssaiNo) b.push({ t: "text", text: `FSSAI: ${s.fssaiNo}`, style: { align: "center" } });
	return b;
}

const WIDE_HEAD: Column[] = [
	{ text: "Item", weight: 22 },
	{ text: "Qty", weight: 4, align: "right" },
	{ text: "Rate", weight: 9, align: "right" },
	{ text: "Amount", weight: 10, align: "right" },
];
const NARROW_HEAD: Column[] = [
	{ text: "Item", weight: 19 },
	{ text: "Qty", weight: 3, align: "right" },
	{ text: "Amount", weight: 8, align: "right" },
];

const withTexts = (head: Column[], texts: string[]): Column[] =>
	head.map((c, i) => ({ ...c, text: texts[i] ?? "" }));

/**
 * A note under an item, indented by an empty column (wrapping drops leading
 * spaces): add-ons and the item's own discount.
 */
const indented = (text: string, value = ""): StoredBlock => ({
	t: "row",
	cols: [
		{ text: "", weight: 2 },
		{ text, weight: 34 },
		{ text: value, weight: 12, align: "right" },
	],
});

function itemBlocks(calc: BillSnapshot["calculation"]): StoredBlock[] {
	const b: StoredBlock[] = [
		{
			t: "byWidth",
			w58: [{ t: "row", cols: NARROW_HEAD, style: { bold: true } }],
			w80: [{ t: "row", cols: WIDE_HEAD, style: { bold: true } }],
		},
		{ t: "rule" },
	];
	for (const line of calc.lines) {
		const name = line.variantName ? `${line.name} (${line.variantName})` : line.name;
		const rate = money(line.unitPricePaise + line.modifiersTotalPaise);
		const qty = String(line.quantity);
		const amount = money(line.grossPaise);
		b.push({
			t: "byWidth",
			w58: [{ t: "row", cols: withTexts(NARROW_HEAD, [name, qty, amount]) }],
			w80: [{ t: "row", cols: withTexts(WIDE_HEAD, [name, qty, rate, amount]) }],
		});
		for (const m of line.modifiers) b.push(indented(`+ ${m.name}`));
		if (line.itemDiscountPaise > 0) {
			b.push(indented(line.discount?.label || "Discount", money(-line.itemDiscountPaise)));
		}
	}
	return b;
}

function discountLabel(calc: BillSnapshot["calculation"]): string {
	const bill = calc.billDiscount;
	if (!bill || calc.totals.itemDiscountPaise > 0) return "Discount";
	if (bill.label) return `Discount (${bill.label})`;
	return bill.rule.type === "percent" ? `Discount ${percent(bill.rule.bp)}` : "Discount";
}

function totalBlocks(snapshot: BillSnapshot): StoredBlock[] {
	const calc = snapshot.calculation;
	const t = calc.totals;
	const items = calc.lines.reduce((n, l) => n + l.quantity, 0);
	const b: StoredBlock[] = [
		sumLine(`Subtotal (${items} ${items === 1 ? "item" : "items"})`, money(t.subtotalPaise)),
	];
	if (t.discountPaise > 0) b.push(sumLine(discountLabel(calc), money(-t.discountPaise)));
	if (calc.serviceCharge && t.serviceChargePaise > 0) {
		b.push(
			sumLine(`Service charge ${percent(calc.serviceCharge.rateBp)}`, money(t.serviceChargePaise)),
		);
	}
	if (snapshot.kind === "tax_invoice") {
		b.push(sumLine("Taxable value", money(t.taxablePaise)));
		for (const tax of calc.taxes) {
			const half = percent(tax.rateBp / 2);
			b.push(sumLine(`CGST ${half}`, money(tax.cgstPaise)));
			b.push(sumLine(`SGST ${half}`, money(tax.sgstPaise)));
		}
	}
	if (t.roundOffPaise !== 0) b.push(sumLine("Round off", signedMoney(t.roundOffPaise)));
	b.push({ t: "rule", ch: "=" });
	// Double width fits 16 characters on 58 mm paper: too few for the amount.
	const total = `₹${money(t.totalPaise)}`;
	b.push({
		t: "byWidth",
		w58: [pair("TOTAL", total, { bold: true, h: 2 }, [1, 2])],
		w80: [pair("TOTAL", total, { bold: true, w: 2, h: 2 }, [1, 2])],
	});
	b.push({ t: "rule", ch: "=" });
	if (snapshot.kind === "tax_invoice" && snapshot.seller.sac) {
		b.push({ t: "text", text: `SAC: ${snapshot.seller.sac}`, style: { align: "center" } });
	}
	if (calc.pricesIncludeTax && snapshot.kind === "tax_invoice") {
		b.push({ t: "text", text: "Prices include GST", style: { align: "center" } });
	}
	return b;
}

function paymentBlocks(snapshot: BillSnapshot, options: BillPrintOptions): StoredBlock[] {
	const payments = options.payments ?? [];
	const total = snapshot.calculation.totals.totalPaise;
	const paid = payments.reduce((n, p) => n + p.amountPaise, 0);
	const b: StoredBlock[] = payments.map((p) =>
		pair(`Paid: ${PAYMENT_LABELS[p.method]}`, money(p.amountPaise)),
	);
	const due = total - paid;
	if (paid > 0 && due > 0) b.push(pair("Balance", money(due), { bold: true }));
	const upi = snapshot.seller.upi;
	if (upi && due > 0) {
		const link = upiLink({
			vpa: upi.vpa,
			payeeName: upi.payeeName,
			amountPaise: due,
			billNo: snapshot.billNo,
			reference: options.billId ? upiReference(options.billId) : null,
		});
		b.push({ t: "feed", lines: 1 });
		b.push({
			t: "text",
			text: `Scan to pay ₹${money(due)} with any UPI app`,
			style: { align: "center", bold: true },
		});
		b.push({
			t: "byWidth",
			w58: [{ t: "qr", data: link, size: 5, align: "center" }],
			w80: [{ t: "qr", data: link, size: 6, align: "center" }],
		});
		b.push({ t: "text", text: upi.vpa, style: { align: "center" } });
	}
	return b;
}

export function billDocument(
	snapshot: BillSnapshot,
	options: BillPrintOptions = {},
): PrintDocument {
	const { date, time } = dateAndTime(snapshot.finalizedAt, snapshot.timeZone);
	const where = snapshot.order.tableLabel
		? `Table: ${snapshot.order.tableLabel}`
		: `Takeaway #${snapshot.order.orderNo}`;
	const blocks: StoredBlock[] = [
		...(options.logoFileId
			? [{ t: "logo" as const, fileId: options.logoFileId, align: "center" as const }]
			: []),
		...sellerBlocks(snapshot.seller),
		{ t: "rule", ch: "=" },
		{ t: "text", text: TITLES[snapshot.kind], style: { align: "center", bold: true } },
		...copyLabelBlock(options.copyLabel),
	];
	if (snapshot.kind === "bill_of_supply") {
		blocks.push({ t: "text", text: COMPOSITION_DECLARATION, style: { align: "center" } });
	}
	blocks.push(pair(`Bill: ${snapshot.billNo}`, date, undefined, [2, 1]), pair(where, time));
	blocks.push(
		pair(
			`Order #${snapshot.order.orderNo}`,
			options.cashierName ? `Cashier: ${options.cashierName}` : "",
		),
	);
	const customer = snapshot.customer;
	if (customer.name) blocks.push({ t: "text", text: `Customer: ${customer.name}` });
	if (customer.gstin) blocks.push({ t: "text", text: `Customer GSTIN: ${customer.gstin}` });
	blocks.push({ t: "rule" });
	blocks.push(...itemBlocks(snapshot.calculation));
	blocks.push({ t: "rule" });
	blocks.push(...totalBlocks(snapshot));
	blocks.push(...paymentBlocks(snapshot, options));
	const footer = centredLines(snapshot.seller.footer);
	if (footer.length > 0) blocks.push({ t: "feed", lines: 1 }, ...footer);
	blocks.push({ t: "cut" });
	return { version: 1, kind: "bill", copyLabel: options.copyLabel ?? null, blocks };
}
